import { observer } from "mobx-react";
import Image from "next/image";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiArrowLeft, FiSearch, FiX } from "react-icons/fi";
import { useStores } from "../../../../stores";
import {
  CoverageAreaDistrict,
  ResellerDistrict,
} from "../../../../stores/coverage-area-store/coverage-area-store.model";
import debounce from "../../../../utils/debounce";
import { Button } from "../../../../components/button";
import {
  BoxedInput,
  LineSelect,
  SelectOption,
} from "../../../../components/forms";
import { IconLocation } from "../../../../components/icons/icon-location";
import { LocationPickerContext, LocationSource, ModalType } from "./context";

export const CityPicker: React.FC<any> = observer(() => {
  const { coverageAreaStore, cartStore } = useStores();
  const { modalType, setModalType, onSubmit } = useContext(
    LocationPickerContext
  );

  const [search, setSearch] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<SelectOption>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption>(null);

  const isMounted = useRef(false);
  const isOpen = useMemo(() => modalType === ModalType.cityPicker, [modalType]);
  const shouldDisableSaveBtn = useMemo(() => {
    return (
      !selectedCity?.key ||
      !selectedDistrict?.key ||
      (search && !coverageAreaStore?.districts?.length)
    );
  }, [
    coverageAreaStore?.districts?.length,
    search,
    selectedCity?.key,
    selectedDistrict?.key,
  ]);

  useEffect(() => {
    if (isOpen) {
      coverageAreaStore?.getCities();
      // SHOULD NOT give default value to dropdown - REMOVE if needed default value
      // if (coverageAreaStore?.selectedCoverageArea?.locationSource === LocationSource.dropdown) {
      //   if (coverageAreaStore?.selectedCity?.id) {
      //     setSelectedCity({ key: String(coverageAreaStore?.selectedCity?.id), value: coverageAreaStore?.selectedCity?.name })
      //   }
      //   if (coverageAreaStore?.selectedDistrict?.id) {
      //     setSelectedDistrict({ key: String(coverageAreaStore?.selectedDistrict?.id), value: coverageAreaStore?.selectedDistrict?.name })
      //   }
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      coverageAreaStore?.getDistricts({ cityId: parseInt(selectedCity?.key) });
    } else {
      isMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity?.key]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchQuery = useCallback(
    debounce(
      async (val: string) =>
        await coverageAreaStore?.getDistricts({ name: val }),
      500
    ),
    []
  );

  const handleSearch = async (val: string) => {
    setSearch(val);
    debouncedSearchQuery(val);
  };

  const handleSelectDistrict = (district: ResellerDistrict) => {
    setSelectedCity({ key: String(district.cityId), value: district.cityName });
    setSelectedDistrict({ key: String(district.id), value: district.name });
    setSearch("");
  };

  const handleSave = async () => {
    // get and set coverage area
    const selectedCoverageArea = await coverageAreaStore?.getAndSetCoverageArea(
      {
        cityId: parseInt(selectedCity?.key),
        districtId: parseInt(selectedDistrict?.key),
        locationSource: LocationSource.dropdown,
      }
    );

    // set customer address
    if (selectedCoverageArea) {
      const savedCheckoutData = await cartStore?.getSavedCheckoutData();
      const { areaDistricts } = selectedCoverageArea;
      const firstAreaDistrict =
        (!!areaDistricts && areaDistricts.length > 0 && areaDistricts[0]) ||
        ({} as CoverageAreaDistrict);
      cartStore?.saveCheckoutData({
        ...savedCheckoutData,
        deliveryType: "delivery",
        ...(selectedCoverageArea && {
          customerAddress: {
            address: `${firstAreaDistrict.cityName} - ${firstAreaDistrict.districtName}`,
            latitude: null,
            longitude: null,
            stateId: firstAreaDistrict.stateId,
            cityId: firstAreaDistrict.cityId,
            districtId: firstAreaDistrict.districtId,
            subdistrictId: null,
            zipcode: null,
            notes: null,
            placeId: null,
          },
        }),
      });
    }
    onSubmit();
  };

  const renderSearchInput = () => {
    return (
      <BoxedInput
        placeholder="Tulis minimal 3 karakter"
        value={search}
        onChange={handleSearch}
        prepend={<FiSearch />}
        append={
          search && (
            <FiX onClick={() => setSearch("")} className="text-primary" />
          )
        }
        appendClassName="cursor-pointer"
        className="py-2 mb-4"
        autoFocus
        id="city-picker-dialog-search-input"
        data-testid="city-picker-dialog-search-input"
      />
    );
  };

  const renderSearchResults = () => {
    return (
      <div
        className="max-h-[375px] overflow-y-auto"
        id="city-picker-dialog-search-item"
        data-testid="city-picker-dialog-search-item"
      >
        {search && coverageAreaStore?.districts?.length > 0
          ? coverageAreaStore?.districts?.map((district) => (
              <div
                key={district.id}
                onClick={() => handleSelectDistrict(district)}
                className="p-3 flex hover:bg-gray-50 cursor-pointer border-b"
              >
                <div className="shrink-0 w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center mr-2">
                  <IconLocation className="text-white" />
                </div>
                <span>
                  {district?.cityName} - {district?.name}
                </span>
              </div>
            ))
          : search && (
              <div className="flex flex-col items-center justify-center">
                <Image
                  src="/img/credimart/map-not-found.svg"
                  alt="Area not found"
                  width="280"
                  height="281"
                />
                <span className="block font-bold text-subtitle1">
                  Wah! Area itu belum ada
                </span>
                <span className="text-gray-500">
                  Cari lokasi pengiriman lainnya
                </span>
              </div>
            )}
      </div>
    );
  };

  const renderSelectDropdown = () => {
    return (
      !search && (
        <>
          <LineSelect
            placeholder="Pilih Kota"
            value={selectedCity}
            options={coverageAreaStore?.cities?.map((c) => ({
              key: String(c.id),
              value: c.name,
            }))}
            onSelect={(val) => {
              setSelectedCity(val);
              if (selectedDistrict) setSelectedDistrict(null);
            }}
            containerClassName="mb-4"
            id="city-picker-dialog-city-select"
          />

          <LineSelect
            placeholder="Pilih Kecamatan"
            value={selectedDistrict}
            options={coverageAreaStore?.districts?.map((c) => ({
              key: String(c.id),
              value: c.name,
            }))}
            onSelect={setSelectedDistrict}
            disabled={!selectedCity}
            id="city-picker-dialog-district-select"
          />
        </>
      )
    );
  };

  return (
    <>
      <div className="flex items-center mb-4">
        <FiArrowLeft
          onClick={() => setModalType(ModalType.locationPicker)}
          className="cursor-pointer text-heading6 mr-2"
          id="city-picker-dialog-back-btn"
          data-testid="city-picker-dialog-back-btn"
        />
        <div className="text-label-xl font-bold">Pilih Kota & Kecamatan</div>
      </div>
      {renderSearchInput()}
      {renderSearchResults()}
      {renderSelectDropdown()}
      <Button
        onClick={handleSave}
        disabled={shouldDisableSaveBtn}
        className="mt-6 w-full"
        id="city-picker-dialog-save-btn"
        data-testid="city-picker-dialog-save-btn"
        size="lg"
      >
        SIMPAN
      </Button>
    </>
  );
});
